"use client";

import { BookOpen, ChevronLeft, ChevronRight, Edit, Share } from "lucide-react";
import { use, useEffect, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input"; // Shadcn UI input component
import { ScrollArea } from "@/components/ui/scroll-area"; // Scroll area for the sidebar
import stoneFAQ from "@/app/_data/faqData";
import { useRouter } from "next/navigation";

// Define the mapping for each section to its respective FAQ anchor
const faqMap: { [key: string]: string } = {
  general: "#general",
  "creating-content": "#creating-content",
  "sharing-content": "#sharing-content",
  "getting-started": "#getting-started",
  api: "#api",
};

export default function DocsPage(props: {
  params: Promise<{ section: string }>;
}) {
  const params = use(props.params);
  const router = useRouter();
  const { section } = params;
  const [activeSection, setActiveSection] = useState<string>(
    section || "general"
  );
  const [searchTerm, setSearchTerm] = useState<string>("");

  const filteredFAQ = {
    general: stoneFAQ.general.filter((faq) =>
      faq.title.toLowerCase().includes(searchTerm.toLowerCase())
    ),
    creatingContent: stoneFAQ.creatingContent.filter((faq) =>
      faq.title.toLowerCase().includes(searchTerm.toLowerCase())
    ),
    sharingContent: stoneFAQ.sharingContent.filter((faq) =>
      faq.title.toLowerCase().includes(searchTerm.toLowerCase())
    ),
  };

  useEffect(() => {
    if (section && faqMap[section]) {
      document
        .querySelector(faqMap[section])
        ?.scrollIntoView({ behavior: "smooth" });
    }
  }, [section]);

  const mainContainerRef = useRef<HTMLDivElement>(null);

  return (
    <div className="flex flex-row h-screen bg-background">
      {/* Left-hand Navbar */}
      <ScrollArea className="w-1/4 h-full md:block hidden p-4 dark:bg-purple-950/20 pt-24">
        <div className="flex flex-col space-y-2">
          <button
            onClick={() => setActiveSection("general")}
            className={`flex items-center p-2 space-x-2 hover:bg-purple-100  hover:dark:bg-purple-700 rounded-md ${
              activeSection === "general"
                ? "bg-purple-200 dark:bg-purple-800"
                : ""
            }`}
          >
            <BookOpen className="w-5 h-5" />
            <span>General</span>
          </button>
          <button
            onClick={() => setActiveSection("creating-content")}
            className={`flex items-center p-2 space-x-2 hover:bg-purple-100  hover:dark:bg-purple-700 rounded-md ${
              activeSection === "creating-content"
                ? "bg-purple-200 dark:bg-purple-800"
                : ""
            }`}
          >
            <Edit className="w-5 h-5" />
            <span>Creating Content</span>
          </button>
          <button
            onClick={() => setActiveSection("sharing-content")}
            className={`flex items-center p-2 space-x-2 hover:bg-purple-100  hover:dark:bg-purple-700 rounded-md ${
              activeSection === "sharing-content"
                ? "bg-purple-200 dark:bg-purple-800"
                : ""
            }`}
          >
            <Share className="w-5 h-5" />
            <span>Sharing Content</span>
          </button>
          {/* <a
            href="#getting-started"
            className={`flex items-center p-2 space-x-2 hover:bg-purple-100  hover:dark:bg-purple-700 rounded-md ${
              activeSection === "getting-started"
                ? "bg-purple-200 dark:bg-purple-800"
                : ""
            }`}
          >
            <FileText className="w-5 h-5" />
            <span>Getting Started</span>
          </a>
          <a
            href="#api"
            className={`flex items-center p-2 space-x-2 hover:bg-purple-100  hover:dark:bg-purple-700 rounded-md ${
              activeSection === "api" ? "bg-purple-200 dark:bg-purple-800" : ""
            }`}
          >
            <Code className="w-5 h-5" />
            <span>API</span>
          </a> */}
        </div>
      </ScrollArea>

      {/* Main Content */}
      <div
        className="flex-grow flex flex-col w-full relative h-full"
        ref={mainContainerRef}
      >
        <div className="mt-[50px] px-4 py-3">
          {/* Search Filter */}
          <div className="flex items-center text-sm space-x-2 my-4">
            <Input
              placeholder="Search documentation..."
              className="rounded-full md:mt-4 bg-zinc-100 dark:bg-[#1e242a] -mb-4 p-4 md:text-lg md:h-[50px]"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <div className="h-full w-full relative overflow-y-auto overflow-x-clip px-4 mb-2">
          {/* FAQ Content */}
          <div>
            {activeSection === "general" && (
              <section id="general" className="pb-3">
                <h2 className="text-2xl font-semibold mt-8 pl-3 pb-8">
                  General
                </h2>
                {filteredFAQ.general.map((faq, index) => (
                  <div
                    key={index}
                    className="my-4 rounded-md p-2 px-6 pb-6 dark:bg-black/20 bg-stone-100"
                  >
                    <h3 className="font-bold my-4 text-lg">{faq.title}</h3>
                    <div
                      className="*:leading-loose"
                      dangerouslySetInnerHTML={{ __html: faq.content }}
                    />
                  </div>
                ))}
                <Button onClick={() => setActiveSection("creating-content")}>
                  Creating Content
                  <ChevronRight />
                </Button>
              </section>
            )}

            {activeSection == "creating-content" && (
              <section id="creating-content" className="pb-3">
                <h2 className="text-2xl font-semibold mt-8 pl-3 pb-8">
                  Creating Content
                </h2>
                {filteredFAQ.creatingContent.map((faq, index) => (
                  <div
                    key={index}
                    className="my-4 rounded-md p-2 px-6 pb-6 dark:bg-black/20 bg-stone-100"
                  >
                    <h3 className="font-bold my-4 text-lg">{faq.title}</h3>
                    <p>{faq.content}</p>
                  </div>
                ))}
                <div className="flex">
                  <Button onClick={() => setActiveSection("sharing-content")}>
                    Sharing Content
                    <ChevronRight />
                  </Button>
                  <Button
                    variant={"ghost"}
                    className="ml-auto"
                    onClick={() => setActiveSection("general")}
                  >
                    General
                    <ChevronLeft />
                  </Button>
                </div>
              </section>
            )}

            {activeSection == "sharing-content" && (
              <section id="sharing-content" className="pb-3">
                <h2 className="text-2xl font-semibold mt-8 pl-3 pb-8">
                  Sharing Content
                </h2>
                {filteredFAQ.sharingContent.map((faq, index) => (
                  <div
                    key={index}
                    className="my-4 rounded-md p-2 px-6 pb-6 dark:bg-black/20 bg-stone-100"
                  >
                    <h3 className="font-bold my-4 text-lg">{faq.title}</h3>
                    <p>{faq.content}</p>
                  </div>
                ))}
                <div className="grid">
                  <Button
                    className="ml-auto"
                    variant={"ghost"}
                    onClick={() => setActiveSection("creating-content")}
                  >
                    Creating Content
                    <ChevronLeft />
                  </Button>
                </div>
              </section>
            )}

            {/* <section id="getting-started">
              <h2 className="text-2xl font-semibold mt-8 pl-3 pb-8">
                Getting Started
              </h2>
              <p>How to set up and create an account.</p>
            </section>

            <section id="api">
              <h2 className="text-2xl font-semibold mt-8 pl-3 pb-8">API</h2>
              <p>API reference and integration guides for advanced users.</p>
            </section> */}
          </div>
        </div>
      </div>
    </div>
  );
}
